import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGetApplicantPayments } from '@/app/pages/applicants/applicant_details/hooks/useGetApplicantPayments'
import PaymentStatusBadge from '@/app/common/components/PaymentStatusBadge'
import { formatDateToUsersTimezone } from '@/lib/utils'
import { AppRoutePaths } from '@/routing/route_paths'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store'
import { ApplicantPayment } from '@/app/apis/Applicants/schema'
import { type RequestError } from '@/common/types_and_interfaces'
import { FamilyApplicationDetails } from '@/queries/family_applications/requests'

const columnHelper = createColumnHelper<ApplicantPayment>()

const columns = [
  columnHelper.accessor('payment_id', {
    id: 'payment_id',
    header: () => (
      <>
        <span className="hidden pl-4 sm:block">{'ID'}</span>
        <span className="block pl-4 sm:hidden">{'ID'}</span>
      </>
    ),
    cell: (info) => {
      const row = info.row.original
      return (
        <div className="flex items-center">
          <div className="w-3/4 sm:w-full font-bold pl-4">
            <div className="font-medium text-gray-900 flex justify-between items-center">
              <span>{row.payment_id}</span>{' '}
              <span className="md:hidden">
                <PaymentStatusBadge value={row.status} />
              </span>
            </div>
            <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
              <div>{(row.student_name ?? []).join(', ')}</div>
              <div>{row.transaction_type}</div>
              <div>{row.provider_name}</div>
            </div>
          </div>
        </div>
      )
    },
  }),
  columnHelper.accessor((row) => `${(row.student_name ?? []).join(', ')} `, {
    id: 'student_name',
    header: () => 'Student Name',
    cell: (info) => <div>{info.getValue()}</div>,
  }),
  columnHelper.accessor('transaction_type', {
    id: 'transaction_type',
    header: () => 'Type',
    cell: (info) => <div>{info.getValue()}</div>,
  }),
  columnHelper.accessor('provider_name', {
    id: 'provider_name',
    header: () => 'Provider',
    cell: (info) => <div>{info.getValue()}</div>,
  }),
  columnHelper.accessor('total_amount', {
    id: 'total_amount',
    header: () => 'Total Amount',
    cell: (info) => <div>{currencyFormatter.format(info.getValue())}</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: () => 'Status',
    cell: (info) => (
      <div>
        <PaymentStatusBadge value={info.getValue()} />
      </div>
    ),
  }),
  columnHelper.accessor('created_datetime', {
    header: () => 'Submitted At',
    cell: (info) => {
      const lastDateTimeStr = info.getValue()
      return (
        <span>
          {lastDateTimeStr ? formatDateToUsersTimezone(lastDateTimeStr) : ''}
        </span>
      )
    },
  }),
  columnHelper.display({
    id: 'action',
    header: () => <span className="pr-2 2xl:p-1">Action</span>,
    cell: (info) => {
      const row = info.row.original

      return (
        <Link
          to={AppRoutePaths.payments.details.index.replace(
            ':paymentId',
            row.payment_id.toString()
          )}
        >
          <Button>Review</Button>
        </Link>
      )
    },
  }),
]

type ApplicantPaymentViewProps = {
  ApplicantData: FamilyApplicationDetails
}

const RECORDS_PER_PAGE = 10 as const
export default function ApplicantPaymentsView({
  ApplicantData,
}: ApplicantPaymentViewProps) {
  const { user_id: applicantId } = ApplicantData
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(
    format(new Date(), 'hh:mm:ss a')
  )

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, selectedProgramYear?.id])

  const { data, error, isError, isLoading, refetch } = useGetApplicantPayments({
    page: currentPage,
    perPage: recordsPerPage,
    search: debouncedSearch,
    programId: selectedProgramYear?.id,
    applicantId: applicantId ?? undefined,
  })

  const payments = data?.data || []
  const totalRows = data?.total || 0
  const lastPage = data?.last_page || 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
    setCurrentPage(1) // Reset to first page when changing items per page
  }

  const handleRefresh = () => {
    refetch()
    setLastUpdatedAt(format(new Date(), 'hh:mm:ss a'))
  }

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: lastPage,
  })

  return (
    <>
      <section aria-labelledby="payment-list-title">
        <h1 id="payment-list-title" className="sr-only">
          Payment list
        </h1>
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Payments
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {`last updated at: ${lastUpdatedAt}`}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Payments"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={isLoading}
                    onClick={handleRefresh}
                  >
                    <CircleArrowsIcon
                      className={cn('text-base', isLoading && 'animate-spin')}
                    />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {`last updated at: ${lastUpdatedAt}`}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          'w px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                          header.column.columnDef.id !== 'payment_id' &&
                            header.column.columnDef.id !== 'action' &&
                            'hidden md:table-cell',
                          header.column.columnDef.id === 'action' && 'text-left'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <PaymentsTableBody
                isLoading={isLoading}
                isError={isError}
                error={error}
                payments={payments}
                columnsLength={columns.length}
                table={table}
              />
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={totalRows}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}

type PaymentsTableBodyProps = {
  isLoading: boolean
  isError: boolean
  error: RequestError | null
  payments: ApplicantPayment[]
  columnsLength: number
  table: ReturnType<typeof useReactTable<ApplicantPayment>>
}

function PaymentsTableBody({
  isLoading,
  isError,
  error,
  payments,
  columnsLength,
  table,
}: PaymentsTableBodyProps) {
  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td colSpan={columnsLength} className="text-center py-4">
            Loading...
          </td>
        </tr>
      </tbody>
    )
  }

  if (isError) {
    return (
      <tbody>
        <tr>
          <td colSpan={columnsLength} className="text-center py-4">
            {error?.message ?? 'Failed to load payments'}
          </td>
        </tr>
      </tbody>
    )
  }

  if (payments.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={columnsLength} className="text-center py-4">
            No payments found
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody>
      {table.getRowModel().rows.map((row) => (
        <tr key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              className={cn(
                ' py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                cell.column.columnDef.id !== 'payment_id' &&
                  cell.column.columnDef.id !== 'action' &&
                  'hidden md:table-cell',
                cell.column.columnDef.id === 'action' && 'text-left'
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}
